import { useDeepCompareEffect, useUnmount } from 'ahooks';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BaseLayerView, BaseStagePlugin, StageScaleView, useFramework } from '@/framework';
import { isSafari } from '@/utils/browser-kit';
import { LayerType, type EditingRange, type ITextLayerProps } from '@/version-types';
import { getAdapter } from '../adapter';
import { EDIT_AREA_ID_SELECTOR, TEXT_EDIT_EVENTS } from '../constants';
import {
    useClipboardEvent,
    useCompositionEvent,
    useInputEvent,
    useKeyboardEvent,
    useMouseEvent,
    useSelectEvent,
} from '../hooks';
import { useTextEditState } from '../states/text-edit-state';
import { getTextContentsStyle, RangeHelper } from '../utils';

export type Ref = {
    $editArea: HTMLDivElement | null;
    $layerViewWrapper: HTMLDivElement | null;
};

const TextEditArea = forwardRef<Ref, {}>((_, ref) => {
    // --- editor hooks ---
    const framework = useFramework();

    const layer = useTextEditState()((state) => state.editingLayer)!;
    const editingRange = useTextEditState()((state) => state.editingRange);
    const setEditingRange = useTextEditState()((state) => state.setEditingRange);

    const layerID = layer.id;
    const layerType = layer.layerType;
    const isBasicOperating = layer.__isBasicOperating;
    const adapter = getAdapter(layerType)!;

    const [renderKey, setRenderKey] = useState(0);

    // --- internal hooks ---
    const { handleCompositionStart, handleCompositionEnd } = useCompositionEvent();
    const { handleSelect } = useSelectEvent();
    const { handleInput } = useInputEvent();
    const { handleKeyDown } = useKeyboardEvent();
    const { handleMouseDown, handleContextMenu } = useMouseEvent();

    // --- layer props ---
    const { uniformText, richText = [] } = adapter.getEditAreaTextContent(framework, layer) || {};
    const isUsingRendererDisplay = adapter.useRendererDisplay();

    /**
     * 可编辑区内渲染的文本内容
     *
     * - 当 `richText.length > 0` 时，渲染富文本内容，即 `richTextContent`
     * - 当 `richText.length === 0` 时，渲染统一样式文本，即 `uniformTextContent`
     */
    const content = (() => {
        // 如果图层直接使用渲染层展示文字，需要将编辑层的内容隐藏
        const spanStyle: React.CSSProperties = isUsingRendererDisplay ? { color: 'transparent' } : {};

        if (richText && richText.length > 0) {
            const { fontFamily } = layer.data as ITextLayerProps;

            const richTextContent = richText.map((item, idx) => {
                const { text, textMark } = item;
                const scopedFontFamily = item.fontFamily || fontFamily;

                const props = {
                    'data-custom': '1',
                    'data-mark': JSON.stringify(textMark),
                    'data-font': scopedFontFamily,
                    'data-color': item.color,
                    style: { ...getTextContentsStyle(item, layer), ...spanStyle },
                };
                return (
                    <span key={idx} {...props}>
                        {text}
                    </span>
                );
            });
            return richTextContent;
        }

        const uniformTextContent = (
            <span data-custom="0" key="-1" style={spanStyle}>
                {uniformText?.endsWith('\n') ? `${uniformText}\n` : uniformText}
            </span>
        );
        return [uniformTextContent];
    })();

    const editAreaWrapperStyle = adapter.getEditAreaWrapperStyle(framework, layer);
    const editAreaCls = classNames('text-edit__edit-area', `text-edit__edit-area--${layer.layerType}`, {
        'safari-hack': isSafari && (layerType === LayerType.TEXT_LAYER || layerType === LayerType.VECTOR_LAYER),
    });
    const editAreaStyles = (() => {
        const styles = adapter.getEditAreaStyle(framework, layer);
        return styles;
    })();

    /**
     * HACK
     *
     * - 对 `content` 的 `ref` 包装
     * - 如果直接用 `content` 作为内容进行渲染时，每次内容更新（input）后，光标跳跃到非期望的位置
     */
    const contentRef = useRef<JSX.Element[]>(content);
    const editAreaRef = useRef<HTMLDivElement>(null);
    const layerViewWrapperRef = useRef<HTMLDivElement>(null);
    const canUpdateManualRef = useRef(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const editingRangeRef = useRef<EditingRange | null>(null);

    const handleBlur = () => {
        canUpdateManualRef.current = true;

        if (adapter.onBlur) adapter.onBlur(framework, layer);
    };

    useClipboardEvent(editAreaRef);

    useImperativeHandle(ref, () => {
        return {
            $editArea: editAreaRef.current,
            $layerViewWrapper: layerViewWrapperRef.current,
        };
    }, []);

    /**
     * 由于内容是通过 `contentRef.current` 承载的，所以当某些属性发生变化时
     * 需要手动更新组件 `div.contentEditable` 内的 DOM
     */
    useDeepCompareEffect(() => {
        if (layerType !== LayerType.TEXT_LAYER) return;
        if (canUpdateManualRef.current || isBasicOperating) {
            canUpdateManualRef.current = false;
            contentRef.current = content;

            // HACK: 缓存全选选区数据，防止全选时设置属性后丢失选区
            // 如果在设置属性之前是全选状态，那么在业务上会被认为是没选（在 use-select-event.ts 中）
            // 此时 store 中的 editingRange 会是 null,故 editingRangeRef.current 会是 null
            // 但是在富文本数据更新后（手动更新 DOM），由于编辑区内容发生变化，选区会丢失
            // 因为在更新之前且是全选的场景下，需要先实时获取选区进行缓存，从而在更新后能够正确地恢复选区
            if (!editingRange) {
                const container = editAreaRef.current;
                if (!container) return;

                const realEditingRange = RangeHelper.getEditingRange(container);
                if (realEditingRange) {
                    const isSelectAll = (() => {
                        return (
                            realEditingRange.startIndex === 0 &&
                            realEditingRange.startOffset === 0 &&
                            realEditingRange.isEnd
                        );
                    })();
                    if (isSelectAll) editingRangeRef.current = realEditingRange;
                }
            }

            setRenderKey((v) => v + 1);

            // 基础操作时不需要重选文本
            if (isBasicOperating) return;

            timerRef.current = setTimeout(() => {
                const container = editAreaRef.current;
                if (!container) return;

                const editingRange = editingRangeRef.current;
                if (editingRange) RangeHelper.applyEditingRange(container, editingRange);
            }, 0);
        }
    }, [richText, layerType, isBasicOperating]);

    // 监听 editingRange 变化，同步到 ref
    useEffect(() => {
        editingRangeRef.current = editingRange;
    }, [editingRange]);

    // 监听是否允许手动更新
    useEffect(() => {
        const listenerID = framework.events.on(TEXT_EDIT_EVENTS.ALLOW_UPDATE_MANUAL, () => {
            canUpdateManualRef.current = true;
        });
        return () => {
            framework.events.off(listenerID);
        };
    }, []);

    // umount
    useUnmount(() => {
        canUpdateManualRef.current = false;

        editingRangeRef.current = null;
        setEditingRange(null);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    });

    const EditableArea = (
        <div
            id={EDIT_AREA_ID_SELECTOR}
            key={renderKey}
            className={editAreaCls}
            style={editAreaStyles}
            ref={editAreaRef}
            contentEditable
            suppressContentEditableWarning
            onContextMenu={handleContextMenu}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onSelect={handleSelect}
            onBeforeInput={() => (canUpdateManualRef.current = false)}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onMouseDown={handleMouseDown}
        >
            {contentRef.current}
        </div>
    );

    return (
        <BaseStagePlugin originIndexOrLayerId={layerID} name="text-edit" style={{ overflow: 'unset' }}>
            <StageScaleView originIndex={framework.tryGetLayerOriginIndex(layerID) || 0}>
                <BaseLayerView disableEvents disableReactiveArea layer={layer} ref={layerViewWrapperRef}>
                    {editAreaWrapperStyle ? <div style={editAreaWrapperStyle}>{EditableArea}</div> : EditableArea}
                </BaseLayerView>
            </StageScaleView>
        </BaseStagePlugin>
    );
});

export default TextEditArea;
